import React from 'react';
import { Brain, Library, Wifi, WifiOff, Eye, Loader2, Sparkles, RotateCcw } from 'lucide-react';
import { CollapsibleCard, CollapseControl } from './CollapsibleCard';
import { Model, Prompt } from '../types';
import { supportsThinkingMode } from '../utils/helpers';
import { useAIConfig } from '../context/AIConfigContext';
import { pickGeminiModel } from '../hooks/useAIProvider';
import { DEFAULT_GEMINI_MODEL } from '../../../config';
import { groupGeminiModels, type GeminiCategory } from '../../../api/catalog/geminiModels';

/** Option label with plain-text markers (<option> cannot hold styled badges). */
export const geminiOptionLabel = (model: Model): string => {
  const tags: string[] = [];
  if (model.id === DEFAULT_GEMINI_MODEL) tags.push('zalecany');
  if (model.isNewest) tags.push('najnowszy');
  if (model.channel === 'preview') tags.push('preview');
  if (model.channel === 'exp') tags.push('eksperymentalny');
  if (model.isAlias) tags.push('alias');
  return tags.length > 0 ? `${model.name} · ${tags.join(' · ')}` : model.name;
};

/** <optgroup> per catalog category, newest first (order comes from the backend). */
const GeminiModelOptions: React.FC<{ models: Model[]; categories?: GeminiCategory[]; extraId?: string }> = ({
  models,
  categories,
  extraId,
}) => {
  const groups = groupGeminiModels(models, categories && categories.length > 0 ? categories : undefined);
  return (
    <>
      {extraId && !models.some((m) => m.id === extraId) && (
        <option value={extraId} className="bg-surface-card text-txt-primary">{extraId}</option>
      )}
      {groups.map(({ category, models: groupModels }) => (
        <optgroup key={category.id} label={category.label}>
          {groupModels.map((model) => (
            <option key={model.id} value={model.id} className="bg-surface-card text-txt-primary">
              {geminiOptionLabel(model)}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
};

const Badge: React.FC<{ children: React.ReactNode; tone?: 'accent' | 'amber' }> = ({ children, tone = 'accent' }) => (
  <span
    className={`not-italic inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wider border ${
      tone === 'amber'
        ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800'
        : 'bg-surface-card text-accent-strong border-accent-border'
    }`}
  >
    {children}
  </span>
);

interface AIControlsProps {
  promptLibrary: Prompt[];
  selectedPromptId: string;
  setSelectedPromptId: (id: string) => void;
  setShowPromptLibrary: (show: boolean) => void;
  enableStreaming: boolean;
  setEnableStreaming: (enable: boolean) => void;
  isStreaming: boolean;
  preparePrompt: (showPreview?: boolean) => string | void;
  generateInterpretation: () => void;
  onRetryGeneration: () => void;
  canRetry: boolean;
  loading: boolean;
  loadingMessage: string;
  collapse?: CollapseControl;
}

export const AIControls: React.FC<AIControlsProps> = ({
  promptLibrary,
  selectedPromptId,
  setSelectedPromptId,
  setShowPromptLibrary,
  enableStreaming,
  setEnableStreaming,
  isStreaming,
  preparePrompt,
  generateInterpretation,
  onRetryGeneration,
  canRetry,
  loading,
  loadingMessage,
  collapse
}) => {
  // AI configuration (provider/model/thinking/connection) comes from context
  // instead of ~25 drilled props (wave 2.3).
  const {
    aiConnectionStatus,
    testAIConnection,
    isTestingConnection,
    selectedProvider,
    setSelectedProvider,
    selectedModel,
    setSelectedModel,
    availableModels,
    geminiModels,
    geminiCategories,
    modelNotice,
    loadFreeModels,
    loadGeminiModels,
    loadingModels,
    fallbackModel,
    setFallbackModel,
    enableThinking,
    setEnableThinking,
    thinkingBudget,
    setThinkingBudget
  } = useAIConfig();

  return (
    <CollapsibleCard
      title="Konfiguracja Silnika AI"
      icon={Brain}
      iconClassName="text-accent-strong animate-pulse"
      open={collapse?.open}
      onOpenChange={collapse?.onOpenChange}
      highlight={loading || isStreaming}
      contentClassName="px-5 pb-5 flex flex-col gap-4"
    >
      {/* Prompt Select */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <label className="text-xs font-semibold text-txt-secondary">
            Styl interpretacji:
          </label>
          <button
            onClick={() => setShowPromptLibrary(true)}
            className="px-2.5 py-1 bg-surface-subtle hover:bg-brd text-txt-secondary border border-brd rounded-lg text-[11px] font-semibold flex items-center gap-1 transition-colors duration-200"
          >
            <Library className="w-3.5 h-3.5" />
            Biblioteka promptów
          </button>
        </div>
        <select
          value={selectedPromptId}
          onChange={(e) => setSelectedPromptId(e.target.value)}
          className="w-full bg-surface-card border border-brd rounded-lg px-3 py-2 text-xs text-txt-primary focus:outline-none focus:border-accent focus:ring-4 focus:ring-emerald-500/5 transition-colors duration-200 shadow-sm"
        >
          {promptLibrary.map(prompt => (
            <option key={prompt.id} value={prompt.id} className="bg-surface-card text-txt-primary">
              {prompt.name} {prompt.isCustom ? '(Własny)' : ''}
            </option>
          ))}
        </select>
        <p className="text-[11px] text-txt-secondary mt-1.5 italic leading-normal">
          {promptLibrary.find(p => p.id === selectedPromptId)?.description}
        </p>
      </div>

      {/* AI Connection Status */}
      <div className="p-3 bg-surface-subtle rounded-xl border border-brd flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-txt-secondary">
            Status połączenia z serwerami:
          </span>
          <button
            onClick={testAIConnection}
            disabled={isTestingConnection}
            className="px-2.5 py-1 bg-accent-soft hover:bg-accent-border text-accent-strong border border-accent-border rounded-lg text-[11px] font-semibold flex items-center gap-1 disabled:opacity-50 transition-colors duration-200"
          >
            {isTestingConnection ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin text-accent-strong" />
                Testowanie...
              </>
            ) : 'Sprawdź połączenie'}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-0.5">
          <div className="flex items-center gap-1.5 p-1.5 rounded-lg bg-surface-card text-[11px] border border-brd shadow-sm">
            {aiConnectionStatus.openrouter ? (
              <Wifi className="w-3.5 h-3.5 text-accent shrink-0" />
            ) : (
              <WifiOff className="w-3.5 h-3.5 text-rose-500 dark:text-rose-400 shrink-0" />
            )}
            <span className={aiConnectionStatus.openrouter ? 'text-accent-strong font-semibold' : 'text-rose-600 dark:text-rose-400 font-semibold'}>
              OpenRouter: {aiConnectionStatus.openrouter ? 'Połączono' : 'Błąd'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 p-1.5 rounded-lg bg-surface-card text-[11px] border border-brd shadow-sm">
            {aiConnectionStatus.gemini ? (
              <Wifi className="w-3.5 h-3.5 text-accent shrink-0" />
            ) : (
              <WifiOff className="w-3.5 h-3.5 text-rose-500 dark:text-rose-400 shrink-0" />
            )}
            <span className={aiConnectionStatus.gemini ? 'text-accent-strong font-semibold' : 'text-rose-600 dark:text-rose-400 font-semibold'}>
              Gemini API: {aiConnectionStatus.gemini ? 'Połączono' : 'Błąd'}
            </span>
          </div>
        </div>
      </div>

      {/* Provider Selection */}
      <div>
        <label className="block text-xs font-semibold text-txt-secondary mb-2">
          Dostawca chmurowy AI:
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              setSelectedProvider('openrouter');
              if (availableModels.length > 0) {
                setSelectedModel(availableModels[0].id);
              } else {
                setSelectedModel('');
                loadFreeModels();
              }
            }}
            className={`p-3 rounded-lg border flex flex-col items-center justify-center gap-1 text-center transition-colors duration-300 ${
              selectedProvider === 'openrouter'
                ? 'bg-blue-50/80 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 shadow-[0_4px_12px_rgba(59,130,246,0.05)] font-semibold'
                : 'bg-surface-card border-brd text-txt-secondary hover:bg-surface-subtle'
            }`}
          >
            <span className="text-xs font-semibold flex items-center gap-1">🌐 OpenRouter</span>
            <span className="text-[9px] opacity-75">Darmowe modele z bieżącego katalogu</span>
          </button>
          <button
            onClick={() => {
              setSelectedProvider('gemini');
              if (geminiModels.length > 0) {
                // Saved choice if still listed, else DEFAULT_GEMINI_MODEL (the
                // newest Flash heads the list and can return empty content with
                // thinking — QA DEV 2026-06-10).
                setSelectedModel(pickGeminiModel(geminiModels));
              } else {
                setSelectedModel('');
                loadGeminiModels();
              }
            }}
            className={`p-3 rounded-lg border flex flex-col items-center justify-center gap-1 text-center transition-colors duration-300 ${
              selectedProvider === 'gemini'
                ? 'bg-accent-soft border-accent-border text-accent-strong shadow-[0_4px_12px_rgba(16,185,129,0.05)] font-semibold'
                : 'bg-surface-card border-brd text-txt-secondary hover:bg-surface-subtle'
            }`}
          >
            <span className="text-xs font-semibold flex items-center gap-1">⚡ Gemini API</span>
            <span className="text-[9px] opacity-75">Bezpośrednie API Google (rekomendowane)</span>
          </button>
        </div>
      </div>

      {/* Model Selection */}
      <div>
        <label className="block text-xs font-semibold text-txt-secondary mb-2">
          Model językowy:
        </label>
        {selectedProvider === 'openrouter' ? (
          <div className="flex flex-col gap-2">
            <p
              role="note"
              data-testid="openrouter-privacy-warning"
              className="text-[11px] text-rose-800 dark:text-rose-200 p-2 border border-rose-200 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-950/40 rounded-lg leading-normal"
            >
              ⚠️ OpenRouter: dane wysyłane poza UE — nie używaj dla danych prawdziwych pacjentów bez zgody.
            </p>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={availableModels.length === 0}
              className="w-full bg-surface-card border border-brd rounded-lg px-3 py-2 text-xs text-txt-primary focus:outline-none focus:border-accent focus:ring-4 focus:ring-emerald-500/5 transition-colors duration-200 disabled:opacity-50 shadow-sm"
            >
              {availableModels.length === 0 ? (
                <option value="">
                  {loadingModels || isTestingConnection ? 'Ładowanie modeli darmowych...' : 'Brak dostępnych modeli OpenRouter'}
                </option>
              ) : (
                availableModels.map(model => (
                  <option key={model.id} value={model.id} className="bg-surface-card text-txt-primary">
                    {model.name}
                  </option>
                ))
              )}
            </select>
            {selectedModel && availableModels.length > 0 && (
              <div className="text-[11px] text-txt-secondary p-2.5 border border-brd bg-surface-subtle rounded-xl italic leading-normal">
                {(() => {
                  const mdl = availableModels.find(m => m.id === selectedModel);
                  return mdl?.description || (mdl?.context_length ? `Kontekst: ${mdl.context_length.toLocaleString()} tokenów` : 'Model OpenRouter');
                })()}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={geminiModels.length === 0 || loadingModels}
              className="w-full bg-surface-card border border-brd rounded-lg px-3 py-2 text-xs text-txt-primary focus:outline-none focus:border-accent focus:ring-4 focus:ring-emerald-500/5 transition-colors duration-200 disabled:opacity-50 shadow-sm"
            >
              {geminiModels.length === 0 ? (
                <option value="">{loadingModels ? 'Ładowanie modeli Gemini...' : 'Brak załadowanych modeli'}</option>
              ) : (
                <GeminiModelOptions models={geminiModels} categories={geminiCategories} />
              )}
            </select>
            {modelNotice && (
              <p role="status" className="text-[11px] text-amber-800 dark:text-amber-200 p-2 border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/40 rounded-lg leading-normal">
                {modelNotice}
              </p>
            )}
            {selectedModel && geminiModels.length > 0 && (
              <div className="text-[11px] text-accent-strong p-2.5 border border-accent-border bg-accent-soft rounded-xl italic leading-normal flex flex-col gap-1.5">
                {(() => {
                  const mdl = geminiModels.find(m => m.id === selectedModel);
                  const limits = mdl?.inputTokenLimit
                    ? `Limit wejścia: ${mdl.inputTokenLimit.toLocaleString()} tokenów${mdl.outputTokenLimit ? `, wyjścia: ${mdl.outputTokenLimit.toLocaleString()}` : ''}`
                    : null;
                  return (
                    <>
                      {(mdl?.id === DEFAULT_GEMINI_MODEL || mdl?.isNewest || mdl?.channel === 'preview' || mdl?.channel === 'exp') && (
                        <div className="flex flex-wrap gap-1">
                          {mdl?.id === DEFAULT_GEMINI_MODEL && <Badge>Zalecany (domyślny)</Badge>}
                          {mdl?.isNewest && <Badge>Najnowszy</Badge>}
                          {mdl?.channel === 'preview' && <Badge tone="amber">Preview</Badge>}
                          {mdl?.channel === 'exp' && <Badge tone="amber">Eksperymentalny</Badge>}
                        </div>
                      )}
                      <span>{mdl?.description || limits || 'Model Gemini'}</span>
                      {mdl?.description && limits && <span className="not-italic text-txt-secondary">{limits}</span>}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fallback Model Selection - shown only for OpenRouter */}
      {selectedProvider === 'openrouter' && (
        <div className="p-3 bg-amber-50/50 dark:bg-amber-950/40 border border-amber-200/60 dark:border-amber-800 rounded-xl flex flex-col gap-2">
          <label className="block text-xs font-semibold text-amber-800 dark:text-amber-200">
            🔄 Model zapasowy (w razie awarii):
          </label>
          <select
            value={fallbackModel}
            onChange={(e) => setFallbackModel(e.target.value)}
            className="w-full bg-surface-card border border-amber-200 dark:border-amber-800 rounded-lg px-2.5 py-1.5 text-xs text-amber-800 dark:text-amber-200 focus:outline-none focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 transition-colors duration-200 shadow-sm"
          >
            <GeminiModelOptions models={geminiModels} categories={geminiCategories} extraId={fallbackModel} />
          </select>
          {modelNotice && (
            <p className="text-[11px] text-amber-800 dark:text-amber-200 leading-normal">{modelNotice}</p>
          )}
          <p className="text-[9px] text-amber-700/80 dark:text-amber-300/80 italic leading-normal">
            *Przy przeciążeniu lub limitach OpenRoutera system bezszwowo przekieruje zapytanie na wybrane API Gemini.
          </p>
        </div>
      )}

      {/* Thinking Mode Controls - ONLY for Gemini models that support it */}
      {/* Same version-based rule as useAIIntegration and the backend (callGemini). */}
      {selectedProvider === 'gemini' && supportsThinkingMode(selectedModel) && (
        <div className="p-3 bg-accent-soft border border-accent-border rounded-xl flex flex-col gap-2">
          <div className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              id="thinkingMode"
              checked={enableThinking}
              onChange={(e) => setEnableThinking(e.target.checked)}
              className="w-4 h-4 rounded border-brd-strong bg-surface-card text-accent focus:ring-emerald-500/30 cursor-pointer"
            />
            <label htmlFor="thinkingMode" className="text-xs font-semibold text-accent-strong cursor-pointer flex-1">
              🧠 Tryb myślenia klinicznego (Thinking Mode)
            </label>
          </div>
          {enableThinking && (
            <div className="pl-6 flex flex-col gap-1.5 animate-fadeIn">
              <label className="block text-[11px] text-txt-secondary">
                Głębokość rozumowania (budget tokenów):
              </label>
              <select
                value={thinkingBudget}
                onChange={(e) => setThinkingBudget(Number(e.target.value))}
                className="w-full bg-surface-card border border-accent-border rounded-lg px-2 py-1 text-xs text-txt-primary focus:outline-none focus:border-accent shadow-sm"
              >
                <option value={2048} className="bg-surface-card text-txt-primary">Zalecany (2 048 tokenów)</option>
                <option value={-1} className="bg-surface-card text-txt-primary">Automatyczny (4 096 tokenów)</option>
                <option value={1000} className="bg-surface-card text-txt-primary">Niski (1 000 tokenów)</option>
                <option value={5000} className="bg-surface-card text-txt-primary">Średni (5 000 tokenów)</option>
                <option value={10000} className="bg-surface-card text-txt-primary">Maksymalny (10 000 tokenów)</option>
              </select>
            </div>
          )}
        </div>
      )}

      {/* Streaming Toggle */}
      <div className="flex items-center justify-between gap-3 p-1">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="streaming"
            checked={enableStreaming}
            onChange={(e) => setEnableStreaming(e.target.checked)}
            className="w-4 h-4 rounded border-brd-strong bg-surface-card text-accent focus:ring-emerald-500/30 cursor-pointer"
          />
          <label htmlFor="streaming" className="text-xs font-semibold text-txt-secondary cursor-pointer">
            Włącz strumieniowanie (Streaming)
          </label>
        </div>
        {enableStreaming && (
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-surface-subtle border border-brd">
            {isStreaming ? (
              <>
                <Loader2 className="w-3 h-3 text-accent animate-spin" />
                <span className="text-[9px] text-accent-strong font-semibold uppercase tracking-wider">Aktywny</span>
              </>
            ) : (
              <>
                <div className="w-1.5 h-1.5 rounded-full bg-txt-tertiary" />
                <span className="text-[9px] text-txt-secondary font-semibold uppercase tracking-wider">Czuwanie</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2.5 mt-2">
        <button
          onClick={() => preparePrompt(true)}
          disabled={loading}
          className="flex-1 py-2.5 bg-surface-subtle hover:bg-brd text-txt-secondary font-semibold text-xs border border-brd rounded-lg flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Eye className="w-4 h-4 text-accent-strong" />
          Podgląd prompta
        </button>

        <button
          onClick={() => generateInterpretation()}
          disabled={loading}
          className={`flex-[1.5] py-2.5 font-bold text-xs rounded-lg flex items-center justify-center gap-2 transition-colors duration-200 ${
            loading
              ? 'bg-surface-subtle border border-brd text-txt-tertiary cursor-not-allowed'
              : 'bg-accent-strong hover:bg-accent text-white dark:text-emerald-950 shadow-[0_4px_14px_rgba(4,120,87,0.2)]'
          }`}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-txt-tertiary" />
              <span className="animate-pulse">{loadingMessage}</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 text-amber-200" />
              Generuj diagnozę AI
            </>
          )}
        </button>

        {canRetry && (
          <button
            onClick={onRetryGeneration}
            disabled={loading}
            title="Ponów ostatnią generację (np. po błędzie)"
            className="py-2.5 px-3 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-800 dark:text-amber-200 font-semibold text-xs border border-amber-200 dark:border-amber-800 rounded-lg flex items-center justify-center gap-1.5 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Ponów
          </button>
        )}
      </div>
    </CollapsibleCard>
  );
};
